import { loadStripe } from '@stripe/stripe-js';
import { CheckoutReturnedError } from './checkoutErrors';

/**
 * Creates a new stripe session using the provided .env file
 * @returns A new Stripe session
 */
const stripeInit = async () => {
  const apiKey = import.meta.env.VITE_STRIPE_KEY;

  if (!apiKey) throw new Error('Stripe API key not found.');

  // Initialize Stripe
  const stripePromise = loadStripe(apiKey as string);

  // Get Stripe.js instance
  const stripe = await stripePromise;

  if (!stripe) throw new Error('Stripe Checkout could not be initialized.');

  return stripe;
};

/**
 * Redirects the user to the Stripe Checkout page using a given sessionId
 * @param sessionId The Stripe session id
 */
export const redirectToStripeCheckout = async (sessionId: string) => {
  let returned = false;
  let signalReturn!: (reason: CheckoutReturnedError) => void;
  const checkoutReturn = new Promise<never>((_resolve, reject) => {
    signalReturn = reject;
  });
  const recoverCheckout = (event: PageTransitionEvent) => {
    if (!event.persisted) return;
    returned = true;
    signalReturn(new CheckoutReturnedError());
  };
  window.addEventListener('pageshow', recoverCheckout);

  try {
    await Promise.race([
      (async () => {
        const stripe = await stripeInit();
        // Initialization may finish after the cached page has already returned.
        if (returned) throw new CheckoutReturnedError();
        const result = await stripe.redirectToCheckout({ sessionId });
        throw new Error(
          result.error.message || 'Stripe Checkout could not be opened.'
        );
      })(),
      checkoutReturn,
    ]);
  } finally {
    window.removeEventListener('pageshow', recoverCheckout);
  }
};
